import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { authApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button, Card, Input } from "../components/ui";

function AuthLayout({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid min-h-full bg-[#f7f9fc] lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-[#102b57] lg:block">
        <picture>
          <source media="(min-width: 1024px)" srcSet="/brand/vouch-hero-desktop.webp" />
          {/* A phone has no artwork panel. A tiny inline fallback keeps the
              browser from downloading the hidden desktop illustration. */}
          <img
            src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="
            alt=""
            width={1280}
            height={548}
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover opacity-85"
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-[#071a37]/80 via-transparent to-[#102b57]/10" />
        <div className="absolute bottom-10 left-10 right-10 text-white"><p className="text-sm font-bold uppercase tracking-[0.16em] text-teal-300">Your financial record</p><p className="mt-2 max-w-lg text-3xl font-black leading-tight">One clear timeline for your money, shared commitments, and what comes next.</p></div>
      </div>
      <div className="flex items-center justify-center p-5 sm:p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 inline-flex items-center gap-2"><img src="/brand/vouch-mark.webp" alt="" width={96} height={88} className="h-11 w-11 object-contain" /><span className="text-xl font-extrabold text-[#102b57]">Vouch</span></Link>
        <div className="mb-5"><h1 className="text-3xl font-black tracking-tight text-slate-900">{title}</h1><p className="mt-1 text-sm text-slate-500">Clear records. Shared confidence.</p></div>
        <Card className="p-6 shadow-lg shadow-slate-900/5">{children}</Card>
      </div>
      </div>
    </div>
  );
}

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const isEmail = identifier.includes("@");
      await login({ [isEmail ? "email" : "username"]: identifier, password });
      nav("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Welcome back">
      <form onSubmit={submit} className="space-y-3">
        <Input
          label="Email or username"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          autoComplete="username"
        />
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <div className="flex justify-between text-sm text-slate-500">
          <Link to="/forgot-password" className="hover:text-brand-500">
            Forgot password?
          </Link>
          <Link to="/register" className="hover:text-brand-500">
            Create account
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function RegisterPage(): JSX.Element {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", username: "", email: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register(form);
      nav("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Create your account">
      <form onSubmit={submit} className="space-y-3">
        <Input label="Full name" value={form.name} onChange={set("name")} />
        <Input label="Username" value={form.username} onChange={set("username")} />
        <Input label="Email" type="email" value={form.email} onChange={set("email")} />
        <Input
          label="Password"
          type="password"
          value={form.password}
          onChange={set("password")}
          autoComplete="new-password"
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? "Creating…" : "Create account"}
        </Button>
        <p className="text-center text-sm text-slate-500">
          Already have an account?{" "}
          <Link to="/login" className="hover:text-brand-500">
            Sign in
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <AuthLayout title="Reset your password">
      {sent ? (
        <p className="text-sm text-slate-600">
          If that email is registered, a reset link is on its way. Check your inbox.
        </p>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await authApi.forgotPassword(email);
            setSent(true);
          }}
          className="space-y-3"
        >
          <Input label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit" className="w-full">
            Send reset link
          </Button>
          <p className="text-center text-sm text-slate-500">
            <Link to="/login" className="hover:text-brand-500">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthLayout title="Choose a new password">
      {done ? (
        <p className="text-sm text-slate-600">
          Password updated. <Link to="/login" className="text-brand-500">Sign in</Link>.
        </p>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await authApi.resetPassword(token, password);
              setDone(true);
            } catch (err) {
              setError(err instanceof Error ? err.message : "Reset failed");
            }
          }}
          className="space-y-3"
        >
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && <p className="text-sm text-red-600">{error}</p>}
          <Button type="submit" className="w-full">
            Update password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailPage(): JSX.Element {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"idle" | "ok" | "error">("idle");
  return (
    <AuthLayout title="Verify your email">
      {state === "ok" ? (
        <p className="text-sm text-slate-600">
          Email verified! <Link to="/dashboard" className="text-brand-500">Go to dashboard</Link>.
        </p>
      ) : state === "error" ? (
        <p className="text-sm text-red-600">That link is invalid or expired.</p>
      ) : (
        <Button
          className="w-full"
          onClick={async () => {
            try {
              await authApi.verifyEmail(token);
              setState("ok");
            } catch {
              setState("error");
            }
          }}
        >
          Confirm my email
        </Button>
      )}
    </AuthLayout>
  );
}
